/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, January 12th 2024, 4:09:31 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import TagIcon from "@mui/icons-material/Tag";
import Stack from "@mui/material/Stack";
import { DocumentTemplate, DOCUMENT_NUMBERING_TYPES } from "lib/schemas";
import { NumberingSequenceForm } from "common/components/numbering-sequence";
import { OSActionButton, StyledOSDialog, SwitchFormInput, DropDownFormInput, EmphasisText } from "ui-widgets";

type DocumentNumberConfigProps = {
  documentTemplateTitle: DocumentTemplate["title"];
  numberingSequenceId: DocumentTemplate["sequence"];
  enableDocumentNumbering: DocumentTemplate["enable_document_numbering"];
  documentNumberingType: DocumentTemplate["document_numbering_type"];
};

export const DocumentNumberConfig = ({
  numberingSequenceId,
  documentNumberingType,
  enableDocumentNumbering,
  documentTemplateTitle,
}: DocumentNumberConfigProps) => {
  const usesSequence = documentNumberingType === "sequence";

  return (
    <StyledOSDialog
      popUpTitle="Configure Document Numbering"
      popUpIcon={<TagIcon color="primary" />}
      renderOpenButton={({ handleOpen }) => (
        <OSActionButton variant="contained" onClick={handleOpen}>
          Configure Document Numbering
        </OSActionButton>
      )}
    >
      <Stack spacing={4}>
        <SwitchFormInput<DocumentTemplate>
          label="Enable Document Numbering"
          schema_field_key="enable_document_numbering"
        />
        {enableDocumentNumbering ? (
          <>
            <DropDownFormInput<DocumentTemplate>
              schema_field_key="document_numbering_type"
              options={DOCUMENT_NUMBERING_TYPES}
              label="Document Numbering Type"
            />

            {usesSequence ? (
              <StyledOSDialog
                popUpTitle={
                  <>
                    Configure Numbering Sequence for <EmphasisText>{documentTemplateTitle}</EmphasisText>
                  </>
                }
                popUpIcon={<TagIcon color="primary" />}
                renderOpenButton={({ handleOpen }) => (
                  <OSActionButton variant="outlined" onClick={handleOpen} size="large">
                    Configure Numbering Sequence
                  </OSActionButton>
                )}
              >
                <NumberingSequenceForm numberingSequenceId={numberingSequenceId} />
              </StyledOSDialog>
            ) : null}
          </>
        ) : null}
      </Stack>
    </StyledOSDialog>
  );
};
